import React, { useState, useEffect, useCallback } from 'react';
import { formatTime } from '../../utils/formatDate';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { RefreshCw, X, CheckCircle, WifiOff, Users, Phone, Activity, Settings, AlertTriangle, Check, UtensilsCrossed, Thermometer, Zap, Star } from 'lucide-react';
import { EDTab, EDTable, EDOrder, EDCall, EDWaiter, EDGuest } from '../../types/EventDay';
import { describeEventDayError, eventDayApi, getSocketAuth, type EventDayProblem } from '../../services/EventDayApiClient';
import { createDoorApi, staffCredentials } from '../../services/DoorApiClient';
import { doorGuestToED } from './doorGuestToED';
import GuestCheckIn from './GuestCheckIn';
import SeatingView from './SeatingView';
import WaiterView from './WaiterView';
import FavoursView from './FavoursView';
import KitchenBarView from './KitchenBarView';
import PreflightCard from './PreflightCard';
import '../../styles/components/EventDayDashboard.css';
import EmptyState from '../EmptyState';

// ── Event phases ────────────────────────────────────────────────────
const EVENT_PHASES = [
  'Arrival', 'Cocktail Hour', 'Entrance', 'Introductions',
  'Dinner', 'Cake Cutting', 'Dance Floor', 'Farewell',
];

function PhaseBar({ phase, onPhase }: { phase: number; onPhase: (p: number) => void }) {
  return (
    <div className="ed-phase-bar">
      {EVENT_PHASES.map((name, i) => (
        <button
          key={i}
          className={`ed-phase-step${i === phase ? ' active' : i < phase ? ' done' : ''}`}
          onClick={() => onPhase(i)}
          title={name}
        >
          <span className="ed-phase-dot" />
          <span className="ed-phase-label">{name}</span>
        </button>
      ))}
    </div>
  );
}

// ── Check-in progress bar ────────────────────────────────────────────
function CheckInBar({ checked, total }: { checked: number; total: number }) {
  const pct = total > 0 ? Math.round((checked / total) * 100) : 0;
  const color = pct >= 90 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <div className="ed-checkin-bar">
      <div className="ed-checkin-bar-track">
        <div className="ed-checkin-bar-fill" style={{ /* ds-dynamic: progress width + threshold color from data */ width: `${pct}%`, background: color }} />
      </div>
      <span className="ed-checkin-bar-label" style={{ /* ds-dynamic: threshold color from data */ color }}>
        {checked}/{total} checked in ({pct}%)
      </span>
    </div>
  );
}

// ── Status helpers ───────────────────────────────────────────────────
type TableStatus = 'grey' | 'green' | 'yellow' | 'red';
const STATUS_DOT_CLASS: Record<TableStatus, string> = { green: 'success', yellow: 'warning', red: 'danger', grey: 'muted' };
const STATUS_LABEL: Record<TableStatus, string> = { green: 'Clear', yellow: 'Orders', red: 'Call', grey: 'Empty' };

function timeAgo(ts: string) {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (diff < 1) return 'just now';
  if (diff < 60) return `${diff}m ago`;
  return `${Math.floor(diff / 60)}h ${diff % 60}m ago`;
}

function minutesAgo(ts: string) {
  return Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
}

function getTableStatus(t: EDTable, orders: EDOrder[], calls: EDCall[]): TableStatus {
  if (calls.some(c => c.table_id === t.id)) return 'red';
  if (orders.some(o => o.table_id === t.id)) return 'yellow';
  if (t.waiter_id) return 'green';
  return 'grey';
}

function getFillPct(t: EDTable, guestsByTable: Record<string, EDGuest[]>): number {
  const cap = t.capacity ?? 0;
  if (!cap) return 0;
  return Math.min(1, (guestsByTable[t.id]?.length ?? 0) / cap);
}

// Deliberately blue → purple, never green/amber/red — those are reserved
// for table status (Clear/Orders/Call) in the other view. Reusing them
// here made a green table mean two unrelated things depending on which
// mode happened to be active, with no visual way to tell which.
function heatmapColor(pct: number): { border: string; bg: string } {
  if (pct === 0)  return { border: '#d8cdb8', bg: '#f3ece0' };
  if (pct < 0.5)  return { border: '#93c5fd', bg: 'rgba(147,197,253,0.16)' };
  if (pct < 0.85) return { border: '#6366f1', bg: 'rgba(99,102,241,0.16)' };
  return { border: '#7c3aed', bg: 'rgba(124,58,237,0.18)' };
}

const statusColors: Record<TableStatus, { border: string; bg: string }> = {
  grey:   { border: '#d8cdb8', bg: '#f3ece0' },
  green:  { border: '#86c99a', bg: '#e3f3e7' },
  yellow: { border: '#e0b25c', bg: '#f7ead0' },
  red:    { border: '#e08a94', bg: '#fbe4e6' },
};

// ── Table cell ───────────────────────────────────────────────────────
interface TableCellProps {
  t: EDTable;
  orders: EDOrder[];
  calls: EDCall[];
  waiters: EDWaiter[];
  guestsByTable: Record<string, EDGuest[]>;
  selectedTableId: string | null;
  heatmap: boolean;
  onSelect: (id: string) => void;
  onResolveCall: (id: string) => void;
}

function TableCell({ t, orders, calls, waiters, guestsByTable, selectedTableId, heatmap, onSelect, onResolveCall }: TableCellProps) {
  const status = getTableStatus(t, orders, calls);
  const fillPct = getFillPct(t, guestsByTable);
  const { border, bg } = heatmap ? heatmapColor(fillPct) : statusColors[status];
  const call = calls.find(c => c.table_id === t.id);
  const hasCall = !!call;
  const tableOrd = orders.filter(o => o.table_id === t.id);
  const waiter = waiters.find(w => w.id === t.waiter_id);
  const pending = tableOrd.filter(o => o.status === 'pending').length;
  const gCount = guestsByTable[t.id]?.length ?? 0;
  const isSelected = selectedTableId === t.id;
  const isVip = VIP_KEYWORDS.some(kw => (t.name ?? t.section ?? '').toLowerCase().includes(kw));

  return (
    <div
      className="ed-table-cell"
      onClick={() => onSelect(t.id)}
      style={{ /* ds-dynamic: selection/status-driven colors */
        borderColor: isSelected ? '#c2670a' : border,
        background: isSelected ? '#fef3c7' : bg,
        opacity: status === 'grey' && !heatmap ? 0.7 : 1,
      }}
    >
      {isVip && <span className="ed-vip-star">★</span>}
      {hasCall && (
        <>
          <span className="ed-call-badge">!</span>
          <button
            type="button"
            className="ed-call-resolve-btn"
            title="Resolve call"
            onClick={(e) => { e.stopPropagation(); onResolveCall(call!.id); }}
          >R</button>
        </>
      )}
      <div className="ed-table-number">T{t.number}</div>
      {t.capacity ? <div className="ed-table-cap">{gCount}{gCount !== t.capacity ? `/${t.capacity}` : ''}p</div> : null}
      {t.name && <div className="ed-table-waiter">{t.name}</div>}
      {waiter && !t.name && <div className="ed-table-waiter">{waiter.name.split(' ')[0]}</div>}
      {tableOrd.length > 0 ? <div className="ed-table-orders">{pending}/{tableOrd.length}</div> : null}
    </div>
  );
}

// ── Table grid ───────────────────────────────────────────────────────
interface TableGridProps {
  tables: EDTable[];
  orders: EDOrder[];
  calls: EDCall[];
  waiters: EDWaiter[];
  guestsByTable: Record<string, EDGuest[]>;
  selectedTableId: string | null;
  heatmap: boolean;
  onSelect: (id: string) => void;
  onResolveCall: (id: string) => void;
  onSetup: () => void;
}

function TableGrid({ tables, orders, calls, waiters, guestsByTable, selectedTableId, heatmap, onSelect, onResolveCall, onSetup }: TableGridProps) {
  if (tables.length === 0) {
    return (
      <EmptyState
        compact
        title="No tables configured yet"
        action={(
          <button className="ed-btn-secondary" onClick={onSetup}>
            <Settings size={13} /> Set up tables
          </button>
        )}
      />
    );
  }

  const hasFloorPlans = tables.some(t => t.floor_plan_id != null);
  if (!hasFloorPlans) {
    return (
      <div className="ed-table-grid">
        {tables.map(t => (
          <TableCell key={t.id} t={t} orders={orders} calls={calls} waiters={waiters}
            guestsByTable={guestsByTable} selectedTableId={selectedTableId} heatmap={heatmap} onSelect={onSelect} onResolveCall={onResolveCall} />
        ))}
      </div>
    );
  }

  const groups: { fpId: string | null; name: string; imageUrl?: string; tables: EDTable[] }[] = [];
  const seen = new Set<string | null>();
  for (const t of tables) {
    const fpId = t.floor_plan_id ?? null;
    if (!seen.has(fpId)) {
      seen.add(fpId);
      groups.push({ fpId, name: fpId != null ? (t.floor_plan_name ?? `Floor Plan ${fpId}`) : 'Unassigned', imageUrl: t.floor_plan_image_url || undefined, tables: [] });
    }
    groups.find(g => g.fpId === fpId)!.tables.push(t);
  }
  groups.sort((a, b) => {
    if (a.fpId == null) return 1;
    if (b.fpId == null) return -1;
    return a.name.localeCompare(b.name);
  });

  return (
    <>
      {groups.map(group => (
        <div key={group.fpId ?? 'unassigned'} className="ed-fp-group">
          <div className="ed-fp-group-header">
            {group.imageUrl && (
              <img className="ed-fp-group-thumb"
                src={group.imageUrl.startsWith('http') ? group.imageUrl : `https://gg.ainaeco.uk${group.imageUrl}`}
                alt={group.name} />
            )}
            <span className="ed-fp-group-name">{group.name}</span>
            <span className="ed-fp-group-count">{group.tables.length} tables</span>
          </div>
          <div className="ed-table-grid">
            {group.tables.map(t => (
              <TableCell key={t.id} t={t} orders={orders} calls={calls} waiters={waiters}
                guestsByTable={guestsByTable} selectedTableId={selectedTableId} heatmap={heatmap} onSelect={onSelect} onResolveCall={onResolveCall} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

// ── Table list view (compact rows, same data as the grid) ───────────────
function TableListView({ tables, orders, calls, waiters, guestsByTable, selectedTableId, onSelect }: TableGridProps) {
  if (tables.length === 0) return null;
  return (
    <div className="ed-table-list">
      {tables.map(t => {
        const status = getTableStatus(t, orders, calls);
        const hasCall = calls.some(c => c.table_id === t.id);
        const waiter = waiters.find(w => w.id === t.waiter_id);
        const gCount = guestsByTable[t.id]?.length ?? 0;
        return (
          <div
            key={t.id}
            className={`ed-table-list-row${selectedTableId === t.id ? ' selected' : ''}`}
            onClick={() => onSelect(t.id)}
          >
            <span className={`ed-dot ed-dot-${STATUS_DOT_CLASS[status]}`} title={STATUS_LABEL[status]} />
            <span className="ed-table-list-num">T{t.number}</span>
            <span className="ed-table-list-name">{t.name || t.section || '—'}</span>
            <span className="ed-table-list-cap">{t.capacity ? `${gCount}/${t.capacity}` : '—'}</span>
            <span className="ed-table-list-waiter">{waiter?.name.split(' ')[0] ?? '—'}</span>
            {hasCall && <span className="ed-table-list-call">Call</span>}
          </div>
        );
      })}
    </div>
  );
}

// ── Active Issues panel (calls + stale orders merged, sorted by urgency) ─
function ActiveIssues({ calls, orders, onResolveCall }: {
  calls: EDCall[];
  orders: EDOrder[];
  onResolveCall: (id: string) => void;
}) {
  type Issue =
    | { kind: 'call'; id: string; tableNum: number | string; label: string; minAgo: number; emergency: boolean }
    | { kind: 'order'; id: string; tableNum: number | string | null; label: string; minAgo: number };

  const issues: Issue[] = [
    ...calls.map(c => ({
      kind: 'call' as const,
      id: c.id,
      tableNum: c.table_number ?? c.table_id,
      label: c.reason,
      minAgo: minutesAgo(c.created_at),
      emergency: c.reason === 'emergency',
    })),
    ...orders
      .filter(o => minutesAgo(o.created_at) >= 10)
      .map(o => ({
        kind: 'order' as const,
        id: o.id,
        tableNum: o.table_number ?? o.table_id,
        label: `${o.vendor} order pending`,
        minAgo: minutesAgo(o.created_at),
      })),
  ].sort((a, b) => {
    // emergency calls first, then by age
    const aScore = (a.kind === 'call' && a.emergency ? 1000 : 0) + a.minAgo;
    const bScore = (b.kind === 'call' && b.emergency ? 1000 : 0) + b.minAgo;
    return bScore - aScore;
  });

  const PER_PAGE = 3;
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(issues.length / PER_PAGE);
  const paged = issues.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  if (issues.length === 0) {
    return (
      <div className="ed-issues-panel">
        <div className="ed-panel-title"><AlertTriangle size={11} /> Active Issues</div>
        <div className="ed-panel-empty">All clear — no open issues</div>
      </div>
    );
  }

  return (
    <div className="ed-issues-panel">
      <div className="ed-panel-title"><AlertTriangle size={11} /> Active Issues <span className="ed-issues-count">{issues.length}</span></div>
      {paged.map(issue => (
        <div key={`${issue.kind}-${issue.id}`}
          className={`ed-issue-row${issue.kind === 'call' && issue.emergency ? ' ed-issue-emergency' : issue.kind === 'call' ? ' ed-issue-call' : ' ed-issue-order'}`}>
          <div className="ed-issue-badge">{issue.kind === 'call' ? <Phone size={14} /> : <UtensilsCrossed size={14} />}</div>
          <div className="ed-issue-body">
            <div className="ed-issue-table">T{issue.tableNum}</div>
            <div className="ed-issue-label">{issue.label}</div>
          </div>
          <div className="ed-issue-age">{issue.minAgo}m</div>
          {issue.kind === 'call' && (
            <button className="ed-btn-resolve" onClick={() => onResolveCall(issue.id)}>
              <CheckCircle size={11} /> Resolve
            </button>
          )}
        </div>
      ))}
      {pageCount > 1 && (
        <div className="ed-table-pagination">
          <button className="ed-table-page-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹</button>
          <span className="ed-table-page-label">{page + 1} / {pageCount}</span>
          <button className="ed-table-page-btn" onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page === pageCount - 1}>›</button>
        </div>
      )}
    </div>
  );
}

// ── Food & Drinks Updates + Activity Feed (merged into one panel — the
// feed's own header controls now sit on Food & Drinks Updates' title row,
// and the feed renders as a second scrollable section below) ───────────
const ORDER_STATUS_LABEL: Record<EDOrder['status'], string> = {
  pending: 'Fired',
  preparing: 'Plating',
  complete: 'Ready',
  cancelled: 'Cancelled',
};

type FeedType = 'order' | 'checkin' | 'call';
interface FeedItem { time: string; text: string; type: FeedType; }

interface FoodDrinksUpdatesProps {
  feed: FeedItem[];
  feedFilter: 'all' | FeedType;
  onFeedFilterChange: (f: 'all' | FeedType) => void;
  feedPage: number;
  onFeedPageChange: (p: number) => void;
}

function FoodDrinksUpdates({ feed, feedFilter, onFeedFilterChange, feedPage, onFeedPageChange }: FoodDrinksUpdatesProps) {
  const FEED_PER_PAGE = 5;
  const visibleFeed = feedFilter === 'all' ? feed : feed.filter(f => f.type === feedFilter);
  const feedPageCount = Math.ceil(visibleFeed.length / FEED_PER_PAGE);
  const pagedFeed = visibleFeed.slice(feedPage * FEED_PER_PAGE, (feedPage + 1) * FEED_PER_PAGE);

  return (
    <div className="ed-issues-panel ed-fd-feed-panel">
      <div className="ed-feed-header">
        <div className="ed-panel-title"><Activity size={11} /> Activity Feed</div>
        <div className="ed-feed-tabs">
          {(['all', 'order', 'checkin', 'call'] as const).map(t => (
            <button
              key={t}
              type="button"
              className={`ed-feed-tab${feedFilter === t ? ' active' : ''}`}
              onClick={() => { onFeedFilterChange(t); onFeedPageChange(0); }}
            >
              {t === 'all' ? 'All' : t === 'order' ? 'Orders' : t === 'checkin' ? 'Check-ins' : 'Calls'}
            </button>
          ))}
        </div>
      </div>

      <div className="ed-fd-activity-section">
        {visibleFeed.length === 0 ? (
          <div className="ed-panel-empty">No recent activity</div>
        ) : (
          <>
            {pagedFeed.map((item, i) => (
              <div key={i} className={`ed-feed-item ed-feed-item--${item.type}`}>
                <span className="ed-feed-icon">
                  {item.type === 'checkin' ? <Users size={11} /> : item.type === 'call' ? <Phone size={11} /> : <UtensilsCrossed size={11} />}
                </span>
                <span className="ed-feed-time">{item.time}</span>
                {item.text}
              </div>
            ))}
            {feedPageCount > 1 && (
              <div className="ed-table-pagination">
                <button className="ed-table-page-btn" onClick={() => onFeedPageChange(Math.max(0, feedPage - 1))} disabled={feedPage === 0}>‹</button>
                <span className="ed-table-page-label">{feedPage + 1} / {feedPageCount}</span>
                <button className="ed-table-page-btn" onClick={() => onFeedPageChange(Math.min(feedPageCount - 1, feedPage + 1))} disabled={feedPage === feedPageCount - 1}>›</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── VIP Radar ────────────────────────────────────────────────────────
const VIP_KEYWORDS = ['vip', 'vvip', 'head', 'bride', 'groom', 'family'];

function VipRadar({ tables, guestsByTable }: {
  tables: EDTable[];
  guestsByTable: Record<string, EDGuest[]>;
}) {
  const vipTables = tables.filter(t =>
    VIP_KEYWORDS.some(kw => (t.name ?? t.section ?? '').toLowerCase().includes(kw))
  );
  if (vipTables.length === 0) return null;

  const missing: { tableName: string; guest: EDGuest }[] = [];
  let vipGuestCount = 0;
  for (const t of vipTables) {
    const tGuests = guestsByTable[t.id] ?? [];
    for (const g of tGuests) {
      vipGuestCount++;
      if (g.checkin_status !== 'TRUE') {
        missing.push({ tableName: t.name ?? `T${t.number}`, guest: g });
      }
    }
  }

  return (
    <div className="ed-vip-radar">
      {vipGuestCount === 0 ? (
        <div className="ed-vip-radar-header-row">
          <h4>VIP Radar</h4>
          <span className="ed-vip-radar-clear ed-vip-radar-clear-row ed-vip-radar-clear-row--dim">
            No VIP guests assigned yet
          </span>
        </div>
      ) : missing.length === 0 ? (
        <div className="ed-vip-radar-header-row">
          <h4>VIP Radar</h4>
          <span className="ed-vip-radar-clear ed-vip-radar-clear-row">
            All VIP guests checked in <Check size={14} />
          </span>
        </div>
      ) : (
        <h4>VIP Radar</h4>
      )}
      {missing.length !== 0 && (
        <div className="ed-vip-list">
          {missing.map(({ tableName, guest }) => (
            <div key={guest.id} className="ed-vip-row">
              <span className="ed-vip-dot" />
              <span className="ed-vip-name">{guest.full_name}</span>
              <span className="ed-vip-table">{tableName}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main dashboard ───────────────────────────────────────────────────
interface EventDayDashboardProps {
  activeTab?: EDTab;
  onVendorOrderCount?: (count: number) => void;
  onVendorRefreshRef?: (fn: (() => void) | null) => void;
  selectedEventId?: string | null;
  onSelectedEventChange?: (id: string) => void;
  waiterMode?: 'orders' | 'assignments';
  onWaiterModeChange?: (mode: 'orders' | 'assignments') => void;
  heatmap?: boolean;
  onHeatmapChange?: (v: boolean) => void;
  kbViewMode?: 'kanban' | 'batch';
  onKbViewModeChange?: (m: 'kanban' | 'batch') => void;
  phase?: number;
  onPhaseChange?: (p: number) => void;
}

const doorApi = createDoorApi(staffCredentials);

const EventDayDashboard: React.FC<EventDayDashboardProps> = ({
  activeTab = 'overview',
  onVendorOrderCount,
  onVendorRefreshRef,
  selectedEventId,
  onSelectedEventChange,
  waiterMode,
  onWaiterModeChange,
  heatmap = false,
  onHeatmapChange,
  kbViewMode,
  onKbViewModeChange,
  phase: externalPhase,
  onPhaseChange,
}) => {
  const [tables, setTables] = useState<EDTable[]>([]);
  const [orders, setOrders] = useState<EDOrder[]>([]);
  const [calls, setCalls] = useState<EDCall[]>([]);
  const [guests, setGuests] = useState<EDGuest[]>([]);
  const [waiters, setWaiters] = useState<EDWaiter[]>([]);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [tableGuests, setTableGuests] = useState<EDGuest[]>([]);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [feedFilter, setFeedFilter] = useState<'all' | FeedType>('all');
  const [feedPage, setFeedPage] = useState(0);
  const [connected, setConnected] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<EventDayProblem | null>(null);
  // Orders and calls are a side list of the overview: when they cannot load (for example the orders table is not set up yet) the
  // tables still show, with this notice instead.
  const [ordersProblem, setOrdersProblem] = useState<EventDayProblem | null>(null);
  const [internalPhase, setInternalPhase] = useState(0);
  const phase = externalPhase ?? internalPhase;
  const setPhase = (p: number) => { setInternalPhase(p); onPhaseChange?.(p); };
  const [tablePage, setTablePage] = useState(0);
  const [recentGuestsPage, setRecentGuestsPage] = useState(0);
  const [tableView, setTableView] = useState<'map' | 'list'>('map');
  // Fullscreen toggle button lives in Header.tsx (right after "Status" in
  // the Event Day toolbar) since that's the component that owns the top
  // header this hides — it dispatches this event rather than a prop since
  // this dashboard has no direct prop access to that sibling component.
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onFullscreen = (e: Event) => setIsFullscreen((e as CustomEvent<boolean>).detail);
    window.addEventListener('event-day-fullscreen', onFullscreen);
    return () => window.removeEventListener('event-day-fullscreen', onFullscreen);
  }, []);

  // Derive guests-by-table map from the full guest list
  const guestsByTable: Record<string, EDGuest[]> = {};
  for (const g of guests) {
    if (g.table_id) {
      if (!guestsByTable[g.table_id]) guestsByTable[g.table_id] = [];
      guestsByTable[g.table_id].push(g);
    }
  }

  const addActivity = useCallback((text: string, type: FeedType = 'order') => {
    const time = formatTime(new Date());
    setFeed(prev => [{ time, text, type }, ...prev].slice(0, 20));
  }, []);

  const feedSeededRef = React.useRef(false);
  // Reconciles event-day tables from venue floor plans, not just once when
  // empty — floor plans get edited after the initial setup. Throttled so a
  // 30s poll cycle or socket burst doesn't refetch every venue on each tick.
  const tableSyncRef = React.useRef(0);
  const TABLE_SYNC_INTERVAL_MS = 5 * 60_000;

  const loadAll = useCallback(async () => {
    try {
      setError(null);
      const dueForSync = Date.now() - tableSyncRef.current > TABLE_SYNC_INTERVAL_MS;
      if (dueForSync) {
        try {
          const { syncTablesFromFloorPlans } = await import('../../services/EventDaySync');
          await syncTablesFromFloorPlans();
          // Only mark the throttle window as consumed on success — a failed
          // attempt (auth hiccup, backend blip) should retry on the very
          // next load instead of going silent for 5 minutes.
          tableSyncRef.current = Date.now();
        } catch { /* no floor plans yet, or sync failed — table list below still loads */ }
      }
      const [tData, oRes, cRes, gRes, wRes] = await Promise.all([
        eventDayApi.getTables(),
        eventDayApi.getOrders().then(d => ({ ok: true as const, d }), (e: unknown) => ({ ok: false as const, e })),
        eventDayApi.getCalls().then(d => ({ ok: true as const, d }), (e: unknown) => ({ ok: false as const, e })),
        selectedEventId
          ? doorApi.listGuests(selectedEventId).then(list => ({ guests: list.map(doorGuestToED) }), () => ({ guests: [] as EDGuest[] }))
          : Promise.resolve({ guests: [] as EDGuest[] }),
        eventDayApi.getWaiters().then(d => d, () => ({ waiters: [] })),
      ]);
      const oData = oRes.ok ? oRes.d : { orders: [] };
      const cData = cRes.ok ? cRes.d : { calls: [] };
      const gData = gRes;
      const wData = wRes;
      setOrdersProblem(!oRes.ok ? describeEventDayError(oRes.e) : !cRes.ok ? describeEventDayError(cRes.e) : null);
      const loadedTables: EDTable[] = tData.tables || [];
      setTables(loadedTables);

      const allOrders = (oData.orders || []) as EDOrder[];
      setOrders(allOrders.filter(o => ['pending', 'preparing'].includes(o.status)));
      setCalls(cData.calls || []);
      setGuests(gData.guests || []);
      setWaiters((wData.waiters || []) as EDWaiter[]);

      if (!feedSeededRef.current) {
        feedSeededRef.current = true;
        try {
          const aData = await eventDayApi.getActivity(4, 20);
          if ((aData.events || []).length > 0) {
            setFeed(aData.events.map(e => ({
              time: formatTime(e.timestamp),
              text: e.text,
              type: /check-?in/i.test(e.text) ? 'checkin' as const : /call/i.test(e.text) ? 'call' as const : 'order' as const,
            })));
          }
        } catch {
          // activity endpoint unavailable — feed starts empty, WS will populate it
        }
      }
    } catch (err) {
      setError(describeEventDayError(err));
    } finally {
      setLoading(false);
    }
  }, [selectedEventId]);

  useEffect(() => {
    if (activeTab !== 'overview') return;
    loadAll();
    const interval = setInterval(loadAll, 30000);
    return () => clearInterval(interval);
  }, [loadAll, activeTab]);

  // Floor plan edits push this instead of waiting on the 5-minute sync
  // throttle — reset it so the very next loadAll() treats a sync as due.
  useEffect(() => {
    const onTablesChanged = () => { tableSyncRef.current = 0; if (activeTab === 'overview') loadAll(); };
    window.addEventListener('floor-plan-tables-changed', onTablesChanged);
    return () => window.removeEventListener('floor-plan-tables-changed', onTablesChanged);
  }, [loadAll, activeTab]);

  useEffect(() => {
    if (activeTab !== 'overview') return;
    window.dispatchEvent(new CustomEvent('event-day-status', { detail: { connected } }));
  }, [connected, activeTab]);

  useEffect(() => {
    if (activeTab !== 'overview') return;
    const s = io(eventDayApi.getBaseUrl(), { auth: getSocketAuth() });
    s.on('connect', () => setConnected(true));
    s.on('disconnect', () => setConnected(false));
    s.on('order:created', (o: EDOrder) => { addActivity(`New ${o.vendor} order — T${o.table_number ?? o.table_id}`); loadAll(); });
    s.on('order:updated', (o: EDOrder) => { addActivity(`${o.vendor} order ${o.status} — T${o.table_number ?? o.table_id}`); loadAll(); });
    s.on('door:changed', (e: { kind: string; guest?: { name?: string } }) => {
      const who = e.guest?.name ?? 'A guest';
      const text = e.kind === 'check_in' ? `${who} checked in` : e.kind === 'undo' ? `${who} check-in undone` : e.kind === 'walk_in' ? `${who} walked in` : null;
      if (text) addActivity(text, 'checkin');
      loadAll();
    });
    s.on('call:new', (c: EDCall) => { addActivity(`[Call] ${c.reason} — T${c.table_id}`); loadAll(); });
    s.on('call:resolved', (c: EDCall) => { addActivity(`[Resolved] Call resolved — T${c.table_id}`); loadAll(); });
    s.on('waiter:assignments-updated', () => loadAll());
    setSocket(s);
    return () => { s.disconnect(); };
  }, [addActivity, loadAll, activeTab]);

  const openDetail = async (tableId: string) => {
    if (selectedTableId === tableId) { setSelectedTableId(null); return; }
    setSelectedTableId(tableId);
    try {
      const gd = await eventDayApi.getTableGuests(tableId, selectedEventId ?? undefined);
      setTableGuests((gd.guests || []).map((g: { id: string; name: string; checked_in_at?: string | null }) => ({
        id: g.id, full_name: g.name, checkin_status: g.checked_in_at ? 'TRUE' : 'FALSE', checkin_time: g.checked_in_at ?? undefined,
      })));
    } catch { setTableGuests([]); }
  };

  const resolveCall = async (callId: string) => {
    try {
      await eventDayApi.resolveCall(callId);
      addActivity(`[Resolved] Call resolved`);
      await loadAll();
    } catch { /* ignore */ }
  };

  const TABLES_PER_PAGE = 12;
  const checkedIn = guests.filter(g => g.checkin_status === 'TRUE').length;
  const checkinPct = guests.length > 0 ? Math.round((checkedIn / guests.length) * 100) : 0;
  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const tablePageCount = Math.ceil(tables.length / TABLES_PER_PAGE);
  const pagedTables = tables.slice(tablePage * TABLES_PER_PAGE, (tablePage + 1) * TABLES_PER_PAGE);
  const tableOrders = orders.filter(o => o.table_id === selectedTableId);
  const tableCall = calls.find(c => c.table_id === selectedTableId);

  if (activeTab === 'checkin') return <div className="ed-dashboard"><GuestCheckIn eventId={selectedEventId} /></div>;
  if (activeTab === 'seating') return <div className="ed-dashboard"><SeatingView eventId={selectedEventId} onEventChange={onSelectedEventChange} /></div>;
  if (activeTab === 'waiter') return <div className="ed-dashboard"><WaiterView mode={waiterMode ?? 'orders'} onModeChange={onWaiterModeChange} eventId={selectedEventId} /></div>;
  if (activeTab === 'kitchen') return <div className="ed-dashboard"><KitchenBarView vendor="food" onOrderCountChange={onVendorOrderCount} onRefreshRef={fn => onVendorRefreshRef?.(fn)} eventId={selectedEventId} viewMode={kbViewMode} onViewModeChange={onKbViewModeChange} /></div>;
  if (activeTab === 'bar') return <div className="ed-dashboard"><KitchenBarView vendor="bar" onOrderCountChange={onVendorOrderCount} onRefreshRef={fn => onVendorRefreshRef?.(fn)} eventId={selectedEventId} viewMode={kbViewMode} onViewModeChange={onKbViewModeChange} /></div>;
  if (activeTab === 'favours') return <div className="ed-dashboard"><FavoursView eventId={selectedEventId} /></div>;

  if (loading) {
    return (
      <div className="ed-dashboard">
        <div className="ed-loading">
          <RefreshCw size={24} className="ed-spin" />
          <p>Connecting to event day backend…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ed-dashboard">
        <div className="ed-error-state" role="alert">
          <WifiOff size={32} />
          <h3>{error.kind === 'offline' ? 'Cannot reach the server' : error.kind === 'forbidden' || error.kind === 'signedOut' ? 'Event Day is not available to this login' : 'Event Day could not load'}</h3>
          <p>{error.message}</p>
          <p>Guest check-in, walk-ins and favours are on the Check-in tab.</p>
          <button className="ed-btn-secondary" onClick={loadAll}>Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`ed-dashboard${isFullscreen ? ' ed-dashboard--fullscreen' : ''}`}>
      {/* Phase bar rendered in breadcrumb header — omitted here */}

      {ordersProblem && (
        <p className="ed-notice" role="status">
          {ordersProblem.kind === 'notSetUp'
            ? 'Kitchen and bar orders are not set up yet. Tables, calls and check-in work.'
            : `Orders and calls could not load. ${ordersProblem.message}`}
        </p>
      )}

      {/* Stats row */}
      {(() => {
        const tablesWithOrders = new Set(orders.map(o => o.table_id)).size;
        const kitchenOrders = orders.filter(o => o.status === 'pending' && o.vendor === 'food').length;
        const barOrders = orders.filter(o => o.status === 'pending' && o.vendor === 'bar').length;
        const overdueCalls = calls.filter(c => Date.now() - new Date(c.created_at).getTime() > 4 * 60_000).length;
        const activeWaiters = waiters.filter(w => w.active);
        const waitersOffDuty = waiters.length - activeWaiters.length;
        const avgWaitMin = pendingOrders > 0
          ? Math.round(orders.filter(o => o.status === 'pending').reduce((sum, o) => sum + (Date.now() - new Date(o.created_at).getTime()), 0) / pendingOrders / 60_000)
          : 0;
        return (
          <div className="ed-stats-row">
            <div className="ed-stat-card">
              <span className="ed-stat-label"><Users size={13} /> Checked In</span>
              <div className="ed-stat-ring-row">
                <svg viewBox="0 0 64 64" className="ed-stat-ring">
                  <circle cx="32" cy="32" r="27" className="ed-stat-ring-track" />
                  <circle
                    cx="32" cy="32" r="27" className="ed-stat-ring-fill"
                    strokeDasharray={`${2 * Math.PI * 27}`}
                    strokeDashoffset={`${2 * Math.PI * 27 * (1 - checkinPct / 100)}`}
                  />
                </svg>
                <span className="ed-stat-value">{checkedIn}<span className="ed-stat-value-sub"> / {guests.length}</span></span>
              </div>
            </div>
            <div className="ed-stat-card">
              <span className="ed-stat-label"><Activity size={13} /> Tables w/ Orders</span>
              <span className="ed-stat-value">{tablesWithOrders}</span>
              <span className="ed-stat-sub">{kitchenOrders} in kitchen · {barOrders} at bar</span>
            </div>
            <div className="ed-stat-card">
              <span className="ed-stat-label"><Phone size={13} /> Open Calls</span>
              <span className="ed-stat-value ed-stat-value--danger">{calls.length}</span>
              {overdueCalls > 0 && <span className="ed-stat-sub ed-stat-sub--danger">{overdueCalls} over 4 min</span>}
            </div>
            <div className="ed-stat-card">
              <span className="ed-stat-label"><Users size={13} /> Active Waiters</span>
              <span className="ed-stat-value">{activeWaiters.length}</span>
              <span className="ed-stat-sub">{waitersOffDuty} off duty</span>
            </div>
            <div className="ed-stat-card">
              <span className="ed-stat-label"><AlertTriangle size={13} /> Pending Orders</span>
              <span className="ed-stat-value">{pendingOrders}</span>
              <span className="ed-stat-sub">avg {avgWaitMin}m wait</span>
            </div>
          </div>
        );
      })()}

      {/* Main 2-column grid */}
      <div className="ed-main-grid">

        {/* Column 1 — table grid + food/drinks updates, stacked */}
        <div className="ed-left-column">
        <div className="ed-table-grid-panel">
          <div className="ed-table-grid-header">
            <div className="header-ed-legend">
              {heatmap ? (
                <>
                  <span><span className="ed-dot ed-dot-heatmap-empty" /> Empty</span>
                  <span><span className="ed-dot ed-dot-heatmap-low" /> &lt;50% full</span>
                  <span><span className="ed-dot ed-dot-heatmap-mid" /> 50–85% full</span>
                  <span><span className="ed-dot ed-dot-heatmap-high" /> 85%+ full</span>
                </>
              ) : (
                <>
                  <span><span className="ed-dot ed-dot-success" /> Clear</span>
                  <span><span className="ed-dot ed-dot-warning" /> Orders</span>
                  <span><span className="ed-dot ed-dot-danger" /> Call</span>
                  <span><span className="ed-dot ed-dot-muted" /> Empty</span>
                </>
              )}
            </div>
            <div className="ed-table-grid-header-actions">
              <div className="ed-table-view-toggle">
                <button className={tableView === 'map' ? 'active' : ''} onClick={() => setTableView('map')}>Map</button>
                <button className={tableView === 'list' ? 'active' : ''} onClick={() => setTableView('list')}>List</button>
                {onHeatmapChange && (
                  <>
                    <div className="ed-table-view-toggle-divider" />
                    <button
                      className={heatmap ? 'active' : ''}
                      onClick={() => onHeatmapChange(!heatmap)}
                      title={heatmap ? 'Switch to status colors' : 'Switch to fill heatmap'}
                    >
                      {heatmap ? <Thermometer size={13} /> : <Zap size={13} />} Heatmap
                    </button>
                  </>
                )}
              </div>
              {tablePageCount > 1 && (
                <div className="ed-table-pagination">
                  <button className="ed-table-page-btn" onClick={() => setTablePage(p => Math.max(0, p - 1))} disabled={tablePage === 0}>‹</button>
                  <span className="ed-table-page-label">{tablePage + 1} / {tablePageCount}</span>
                  <button className="ed-table-page-btn" onClick={() => setTablePage(p => Math.min(tablePageCount - 1, p + 1))} disabled={tablePage === tablePageCount - 1}>›</button>
                </div>
              )}
            </div>
          </div>
          {tableView === 'map' ? (
            <TableGrid
              tables={pagedTables}
              orders={orders}
              calls={calls}
              waiters={waiters}
              guestsByTable={guestsByTable}
              selectedTableId={selectedTableId}
              heatmap={heatmap}
              onSelect={openDetail}
              onResolveCall={resolveCall}
              onSetup={() => navigate('/venue')}
            />
          ) : (
            <TableListView
              tables={pagedTables}
              orders={orders}
              calls={calls}
              waiters={waiters}
              guestsByTable={guestsByTable}
              selectedTableId={selectedTableId}
              heatmap={heatmap}
              onSelect={openDetail}
              onResolveCall={resolveCall}
              onSetup={() => navigate('/venue')}
            />
          )}
        </div>
        <FoodDrinksUpdates
          feed={feed}
          feedFilter={feedFilter}
          onFeedFilterChange={setFeedFilter}
          feedPage={feedPage}
          onFeedPageChange={setFeedPage}
        />
        </div>

        {/* Column 2 — active issues, always visible so selecting a table
            doesn't restructure the sidebar. Table-specific detail (call,
            orders, guest list) lives in the Column 3 card below instead of
            swapping this one out. */}
        <div className="ed-side-column">
        <div className="ed-center-panel">
          <ActiveIssues calls={calls} orders={orders} onResolveCall={resolveCall} />
        </div>

        {/* Column 3 — guests at selected table, or overall check-in stats — one
            unified panel; VIP Radar renders as a sub-section below rather
            than its own stacked card. */}
        <div className="ed-right-panel">
          <div className="ed-panel-card">
            {(() => {
              const checkedIn = guests.filter(g => g.checkin_status === 'TRUE').length;
              const pct = guests.length > 0 ? Math.round((checkedIn / guests.length) * 100) : 0;
              const selTable = selectedTableId ? tables.find(t => t.id === selectedTableId) : null;
              const checkedCount = tableGuests.filter(g => g.checkin_status === 'TRUE').length;
              return (
                <>
                  {/* Always the overall summary — selecting a table no longer
                      replaces this with a differently-shaped panel; the
                      selected table's name/badge/close ride the same row
                      instead of a stacked header below. */}
                  <div className="ed-panel-title ed-guest-title-row">
                    <Users size={18} />
                    <span className="ed-guest-summary-num">{checkedIn}</span>
                    <span>Guests</span>
                    {guests.length > 0 && !selectedTableId && <span className="ed-guest-checkin-caption">check in ({pct}%)</span>}
                    {selectedTableId && (
                      <>
                        <span className="ed-guest-title-sep" />
                        <span className="ed-selected-table-name">
                          {selTable ? `T${selTable.number}${selTable.name ? ` — ${selTable.name}` : ''}` : 'Table'}
                        </span>
                        <span className="ed-guest-count-badge">{checkedCount}/{tableGuests.length}</span>
                        <button className="ed-btn-icon ed-panel-title-close" onClick={() => setSelectedTableId(null)}><X size={14} /></button>
                      </>
                    )}
                  </div>
                </>
              );
            })()}
            {selectedTableId && (tableCall || tableOrders.length > 0) && (() => {
              return (
                <div className="ed-selected-table-detail">
                  {tableCall && (
                    <div className={`ed-call-banner ${tableCall.reason === 'emergency' ? 'ed-call-emergency' : 'ed-call-normal'}`}>
                      <Phone size={13} />
                      {/* Resolve lives in Active Issues (always visible above
                          this panel) and now also directly on the table's
                          Map card — a third button here for the same call
                          was redundant. */}
                      <span>Open call — <strong>{tableCall.reason}</strong> · {timeAgo(tableCall.created_at)}</span>
                    </div>
                  )}
                  {tableOrders.length > 0 && (
                    <div className="ed-table-orders-compact">
                      {tableOrders.map(o => (
                        <div key={o.id} className="ed-order-card">
                          <div className="ed-order-meta">
                            <span className={`ed-badge ed-badge-${o.status === 'pending' ? 'gold' : 'indigo'}`}>{o.status}</span>
                            <span className="ed-badge ed-badge-dim">{o.vendor}</span>
                            <span className="ed-order-time">{timeAgo(o.created_at)}</span>
                          </div>
                          <div className="ed-order-items">
                            {(o.items || []).map((item, idx) => (
                              <span key={idx} className="ed-order-item">{item.quantity}× {item.name}</span>
                            ))}
                          </div>
                          {o.notes && <p className="ed-order-notes">{o.notes}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="ed-guest-checkin-list">
              {selectedTableId ? (
                tableGuests.length === 0 ? (
                  <div className="ed-panel-empty">No guests assigned to this table</div>
                ) : tableGuests.map(g => (
                  <div key={g.id} className={`ed-guest-row${g.checkin_status === 'TRUE' ? ' checked' : ''}`}>
                    <span className="ed-guest-row-name">{g.full_name}</span>
                    <span className="ed-guest-row-status">
                      {g.checkin_status === 'TRUE' ? <Check size={11} /> : '–'}
                    </span>
                  </div>
                ))
              ) : guests.length === 0 ? (
                <div className="ed-panel-empty">No guest data — select a table to see its guests</div>
              ) : (() => {
                const pct = guests.length > 0 ? Math.round((guests.filter(g => g.checkin_status === 'TRUE').length / guests.length) * 100) : 0;
                const RECENT_PER_PAGE = 3;
                const recent = guests
                  .filter(g => g.checkin_status === 'TRUE' && g.checkin_time)
                  .sort((a, b) => new Date(b.checkin_time!).getTime() - new Date(a.checkin_time!).getTime());
                const recentPageCount = Math.ceil(recent.length / RECENT_PER_PAGE);
                const pagedRecent = recent.slice(recentGuestsPage * RECENT_PER_PAGE, (recentGuestsPage + 1) * RECENT_PER_PAGE);
                return (
                  <>
                    <div className="ed-guest-summary-bar">
                      <div className="ed-guest-summary-fill" style={{ /* ds-dynamic: fill width from data */ width: `${pct}%` }} />
                    </div>
                    {recent.length === 0 ? (
                      <div className="ed-panel-empty ed-panel-empty--select-hint">Select a table to see its guest list</div>
                    ) : (
                      <>
                        {pagedRecent.map(g => (
                          <div key={g.id} className="ed-fd-row">
                            <div className="ed-fd-body">
                              <div className="ed-fd-item">{g.full_name}</div>
                            </div>
                            <span className="ed-fd-status ed-fd-status--complete">
                              {formatTime(g.checkin_time!)}
                            </span>
                          </div>
                        ))}
                        {recentPageCount > 1 && (
                          <div className="ed-table-pagination">
                            <button className="ed-table-page-btn" onClick={() => setRecentGuestsPage(p => Math.max(0, p - 1))} disabled={recentGuestsPage === 0}>‹</button>
                            <span className="ed-table-page-label">{recentGuestsPage + 1} / {recentPageCount}</span>
                            <button className="ed-table-page-btn" onClick={() => setRecentGuestsPage(p => Math.min(recentPageCount - 1, p + 1))} disabled={recentGuestsPage === recentPageCount - 1}>›</button>
                          </div>
                        )}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
            <VipRadar tables={tables} guestsByTable={guestsByTable} />
          </div>
        </div>
        </div>
      </div>
      {socket && null}
    </div>
  );
};

export default EventDayDashboard;
